import { useEffect, useState } from "react";
import { useParams, useLocation, useOutletContext } from "react-router-dom";
import {
  Box,
  Typography,
  CircularProgress,
  Stack,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  IconButton,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import useGetAdvancedSurveyResponses from "../../hooks/useGetAdvancedSurveyResponses";
import PatientAdvancedSurveyForm from "../../components/PatientAdvancedSurveyForm";
import useRetakeAdvancedSurveyMutation from "../../hooks/useRetakeAdvancedSurveyMutation";
import CustomButton from "../../components/UI/CustomButton";
import { USER_ROLES } from "../../utils/constants";
import useUserActionsLogsMutation from "../../hooks/useUserActionsLogsMutation";

// Also serves an eDiary occurrence's answers (isEdiary), keeping the eDiary tab active
const PatientAdvancedSurveysDetails = ({ isEdiary = false }) => {
  const { sessionId } = useParams();
  const location = useLocation();
  const visitName =
    location.state?.visitName || new URLSearchParams(location.search).get("visitName");
  const role = JSON.parse(localStorage.getItem("user_data"))?.role;

  const { infoData, setActive, setBreadcrumbTitle } = useOutletContext();

  useEffect(() => {
    setActive(isEdiary ? "ediary" : "advanced-surveys");
    setBreadcrumbTitle(isEdiary ? "eDiary" : "ePRO");
  }, [isEdiary, setActive, setBreadcrumbTitle]);

  const [confirmSurvey, setConfirmSurvey] = useState(null);

  const logAction = useUserActionsLogsMutation();
  const { data: surveyList = [], isLoading } = useGetAdvancedSurveyResponses({
    photoSessionId: sessionId,
  });
  const { mutate: retakeSurvey, isLoading: isRetaking } = useRetakeAdvancedSurveyMutation();

  const handleConfirmRetake = () => {
    retakeSurvey({ advancedResponseId: confirmSurvey.id });
    logAction.mutate({
      action_type: `Clicked on retake survey named: ${confirmSurvey.advanced_survey_name}`,
      trial_name: infoData?.trial_name,
      patient_id: infoData?.email,
    });
    setConfirmSurvey(null);
  };

  const canRetake =
    role === USER_ROLES.INVESTIGATOR || role === USER_ROLES.CRC || role === USER_ROLES.SUPER_ADMIN;

  return (
    <Box p={3}>
      <Typography variant="subtitle1" fontWeight={600} mb={2}>
        Visit Name: {visitName || "-"}
      </Typography>

      {isLoading ? (
        <Box display="flex" justifyContent="center" py={6}>
          <CircularProgress />
        </Box>
      ) : surveyList.length > 0 ? (
        <Stack spacing={2}>
          {surveyList.map((survey) => (
            <Accordion key={survey.id}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Box display="flex" justifyContent="space-between" alignItems="center" width="100%">
                  <Typography fontWeight={600}>{survey.advanced_survey_name}</Typography>

                  {canRetake &&
                    survey.response_json &&
                    Object.keys(survey.response_json).length > 0 && (
                      <CustomButton
                        size="small"
                        disabled={isRetaking}
                        onClick={(e) => {
                          e.stopPropagation();
                          setConfirmSurvey(survey);
                        }}
                      >
                        Retake
                      </CustomButton>
                    )}
                </Box>
              </AccordionSummary>

              <AccordionDetails>
                {survey.response_json &&
                survey.survey_json &&
                Object.keys(survey.response_json).length > 0 ? (
                  <PatientAdvancedSurveyForm
                    surveyJson={survey?.survey_json}
                    answerJson={survey?.response_json}
                  />
                ) : (
                  <Typography color="text.secondary" p={2}>
                    No responses available
                  </Typography>
                )}
              </AccordionDetails>
            </Accordion>
          ))}
        </Stack>
      ) : (
        <Typography textAlign="center" color="text.secondary">
          ePRO not available
        </Typography>
      )}

      {confirmSurvey && (
        <Dialog open onClose={() => setConfirmSurvey(null)} maxWidth="sm" fullWidth>
          <DialogTitle>
            Retake ePRO
            <IconButton
              onClick={() => setConfirmSurvey(null)}
              sx={{ position: "absolute", right: 8, top: 8 }}
            >
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent>
            <Typography align="center">
              The current ePRO will be deleted and replaced with a new one. Continue?
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button variant="outlined" onClick={() => setConfirmSurvey(null)}>
              Cancel
            </Button>
            <Button variant="contained" onClick={handleConfirmRetake}>
              Continue
            </Button>
          </DialogActions>
        </Dialog>
      )}
    </Box>
  );
};

export default PatientAdvancedSurveysDetails;
